import { createContext, useCallback, useEffect, useState } from "react";

import { supabase } from "./supabaseClient";

export const AuthContext = createContext(null);

async function getProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async (nextUser = user) => {
    if (!nextUser?.id) {
      setProfile(null);
      setProfileReady(true);
      return null;
    }

    setProfileReady(false);

    try {
      const nextProfile = await getProfile(nextUser.id);
      setProfile(nextProfile);
      return nextProfile;
    } catch (error) {
      console.error("Profile load error:", error);
      setProfile(null);
      return null;
    } finally {
      setProfileReady(true);
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;

    async function refreshMountedProfile(nextUser) {
      if (!nextUser?.id) {
        setProfile(null);
        setProfileReady(true);
        return;
      }

      setProfileReady(false);

      try {
        const nextProfile = await getProfile(nextUser.id);
        if (!mounted) return;
        setProfile(nextProfile);
      } catch (error) {
        if (!mounted) return;
        console.error("Profile load error:", error);
        setProfile(null);
      } finally {
        if (mounted) setProfileReady(true);
      }
    }

    supabase.auth.getSession().then(async ({ data, error }) => {
      if (!mounted) return;

      if (error) {
        console.error("Auth load error:", error);
        setUser(null);
        setProfile(null);
        setProfileReady(true);
        setLoading(false);
        return;
      }

      const nextUser = data?.session?.user || null;
      setUser(nextUser);
      setLoading(false);
      await refreshMountedProfile(nextUser);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;

      const nextUser = session?.user || null;
      setUser(nextUser);
      setLoading(false);
      await refreshMountedProfile(nextUser);
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        profileReady,
        loading,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
